import React from 'react';
import styled from 'styled-components';
import { 
  FiGlobe, 
  FiUsers, 
  FiAward, 
  FiBarChart2,
  FiHeart,
  FiTarget,
  FiCheckCircle
} from 'react-icons/fi';
// import { FaLinkedin, FaTwitter, FaInstagram } from 'react-icons/fa';
// import Button from '../../components/shared/Button';
// import TeamMemberCard from '../../components/TeamMemberCard';

// Design Tokens
const colors = {
  primary: '#151c48',
  primaryLight: '#2a3a8f',
  accent: '#5ab9d3',
  white: '#ffffff',
  lightBg: '#f8f9ff',
  textPrimary: '#1a1a1a',
  textSecondary: '#5f6c84'
};

const spacing = {
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px'
};

const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px'
};

// Animations
const fadeIn = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Styled Components
const AboutUsContainer = styled.div`
  width: 100%;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: ${colors.textPrimary};
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%);
  color: ${colors.white};
  padding: 140px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80') center/cover;
    opacity: 0.15;
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: ${spacing.xxl};
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${colors.accent};
    margin: ${spacing.md} auto 0;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${colors.textSecondary};
  max-width: 800px;
  margin: 0 auto ${spacing.xxxl};
  text-align: center;
  line-height: 1.6;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: ${spacing.lg};
  line-height: 1.2;
  letter-spacing: -1px;
  animation: ${fadeIn} 0.8s ease-out;
  
  span {
    color: ${colors.accent};
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.4rem;
  max-width: 800px;
  margin: 0 auto ${spacing.xxl};
  opacity: 0.9;
  animation: ${fadeIn} 0.8s ease-out 0.2s forwards;
  opacity: 0;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const MissionVisionSection = styled.section`
  padding: ${spacing.xxxl} 0;
  background: ${colors.white};
`;

const MissionVisionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.xxl};
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MissionVisionCard = styled.div`
  background: ${colors.lightBg};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xxl};
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const MissionVisionIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.accent});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing.lg};
  color: ${colors.white};
  font-size: 1.8rem;
`;

const MissionVisionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${colors.primary};
  margin-bottom: ${spacing.md};
`;

const MissionVisionText = styled.p`
  color: ${colors.textSecondary};
  line-height: 1.6;
`;

const StatsSection = styled.section`
  padding: ${spacing.xxxl} 0;
  background: ${colors.lightBg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacing.xl};
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${colors.white};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xxl};
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: ${spacing.sm};
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: ${colors.textSecondary};
`;

const ValuesSection = styled.section`
  padding: ${spacing.xxxl} 0;
  background: ${colors.white};
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing.xxl};
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ValueIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${colors.lightBg};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.lg};
  color: ${colors.accent};
  font-size: 1.8rem;
`;

const ValueTitle = styled.h4`
  font-size: 1.3rem;
  color: ${colors.primary};
  margin-bottom: ${spacing.md};
`;

const ValueText = styled.p`
  color: ${colors.textSecondary};
  line-height: 1.6;
`;

// const TeamSection = styled.section`
//   padding: ${spacing.xxxl} 0;
//   background: ${colors.lightBg};
// `;

// const TeamGrid = styled.div`
//   display: grid;
//   grid-template-columns: repeat(4, 1fr);
//   gap: ${spacing.xxl};
//   max-width: 1200px;
//   margin: 0 auto;

//   @media (max-width: 1200px) {
//     grid-template-columns: repeat(3, 1fr);
//   }

//   @media (max-width: 992px) {
//     grid-template-columns: repeat(2, 1fr);
//   }

//   @media (max-width: 576px) {
//     grid-template-columns: 1fr;
//   }
// `;

// const CtaSection = styled.section`
//   padding: ${spacing.xxxl} 0;
//   background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%);
//   color: ${colors.white};
//   text-align: center;
// `;

// const CtaTitle = styled.h2`
//   font-size: 2.5rem;
//   margin-bottom: ${spacing.lg};
// `;

// const CtaText = styled.p`
//   font-size: 1.2rem;
//   max-width: 700px;
//   margin: 0 auto ${spacing.xxl};
//   line-height: 1.6;
// `;

// const SocialLinks = styled.div`
//   display: flex;
//   justify-content: center;
//   gap: ${spacing.lg};
//   margin-top: ${spacing.xxl};
// `;

// const SocialLink = styled.a`
//   color: ${colors.white};
//   font-size: 1.5rem;
//   transition: color 0.3s ease;
  
//   &:hover {
//     color: ${colors.accent};
//   }
// `;

const AboutUsPage = () => {
//   const teamMembers = [
//     {
//       id: 1,
//       name: 'Ana Silva',
//       role: 'CEO & Fundadora',
//       bio: 'Especialista em processos de imigração com mais de 10 anos de experiência ajudando famílias a se estabelecerem em Portugal.',
//       photo: 'https://randomuser.me/api/portraits/women/44.jpg'
//     },
//     {
//       id: 2,
//       name: 'Miguel Santos',
//       role: 'CTO',
//       bio: 'Desenvolvedor experiente com paixão por criar soluções tecnológicas que simplificam processos complexos.',
//       photo: 'https://randomuser.me/api/portraits/men/32.jpg'
//     },
//     {
//       id: 3,
//       name: 'Sofia Pereira',
//       role: 'Head de Relacionamento',
//       bio: 'Responsável por garantir a melhor experiência para nossos usuários e parceiros.',
//       photo: 'https://randomuser.me/api/portraits/women/68.jpg'
//     },
//     {
//       id: 4,
//       name: 'Carlos Mendes',
//       role: 'Especialista em Vistos',
//       bio: 'Conhece todos os detalhes do processo de imigração para Portugal e ajuda nossos usuários a navegarem pela burocracia.',
//       photo: 'https://randomuser.me/api/portraits/men/75.jpg'
//     }
//   ];

  return (
    <AboutUsContainer>
      <HeroSection>
        <Container>
          <HeroTitle>
            <span>Transformando sonhos em realidade</span>
          </HeroTitle>
          <HeroSubtitle>
            Na Realoca Fácil, acreditamos que mudar de país deve ser uma experiência positiva e sem estresse. 
            Nossa missão é simplificar cada etapa do processo de realocação para Portugal.
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <MissionVisionSection>
        <Container>
          <SectionTitle>Nossa Missão e Visão</SectionTitle>
          <SectionSubtitle>
            Estamos construindo o futuro da realocação internacional, combinando tecnologia 
            e expertise humana para oferecer a melhor experiência possível.
          </SectionSubtitle>
          
          <MissionVisionGrid>
            <MissionVisionCard>
              <MissionVisionIcon>
                <FiTarget size={28} />
              </MissionVisionIcon>
              <MissionVisionTitle>Missão</MissionVisionTitle>
              <MissionVisionText>
                Facilitar o processo de realocação de estrangeiros para Portugal ao conectar 
                consultores e empresas especializadas com usuários que buscam assistência, 
                oferecendo uma plataforma intuitiva e automatizada.
              </MissionVisionText>
            </MissionVisionCard>
            
            <MissionVisionCard>
              <MissionVisionIcon>
                <FiGlobe size={28} />
              </MissionVisionIcon>
              <MissionVisionTitle>Visão</MissionVisionTitle>
              <MissionVisionText>
                Ser a plataforma líder em conexão entre consultores, empresas de realocação 
                e clientes, tornando-se a referência para quem deseja se mudar para Portugal.
              </MissionVisionText>
            </MissionVisionCard>
          </MissionVisionGrid>
        </Container>
      </MissionVisionSection>

      <StatsSection>
        <Container>
          <SectionTitle>Nossos Números</SectionTitle>
          <SectionSubtitle>
            Estamos orgulhosos do impacto que causamos na vida das pessoas e no mercado de realocação.
          </SectionSubtitle>
          
          <StatsGrid>
            <StatCard>
              <StatNumber>5K+</StatNumber>
              <StatLabel>Pessoas ajudadas</StatLabel>
            </StatCard>
            
            <StatCard>
              <StatNumber>200+</StatNumber>
              <StatLabel>Empresas parceiras</StatLabel>
            </StatCard>
            
            <StatCard>
              <StatNumber>98%</StatNumber>
              <StatLabel>Satisfação dos clientes</StatLabel>
            </StatCard>
            
            <StatCard>
              <StatNumber>3</StatNumber>
              <StatLabel>Países em expansão</StatLabel>
            </StatCard>
          </StatsGrid>
        </Container>
      </StatsSection>

      <ValuesSection>
        <Container>
          <SectionTitle>Nossos Valores</SectionTitle>
          <SectionSubtitle>
            Esses são os princípios que guiam cada decisão que tomamos na Realoca Fácil.
          </SectionSubtitle>
          
          <ValuesGrid>
            <ValueCard>
              <ValueIcon>
                <FiUsers size={24} />
              </ValueIcon>
              <ValueTitle>Foco no Cliente</ValueTitle>
              <ValueText>
                Colocamos as necessidades dos nossos usuários em primeiro lugar, garantindo 
                que cada interação com nossa plataforma seja positiva e útil.
              </ValueText>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <FiCheckCircle size={24} />
              </ValueIcon>
              <ValueTitle>Transparência</ValueTitle>
              <ValueText>
                Acreditamos em processos claros e comunicação aberta, tanto com nossos usuários 
                quanto com nossos parceiros.
              </ValueText>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <FiAward size={24} />
              </ValueIcon>
              <ValueTitle>Excelência</ValueTitle>
              <ValueText>
                Buscamos constantemente melhorar nossos serviços e oferecer a melhor experiência 
                possível em realocação internacional.
              </ValueText>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <FiHeart size={24} />
              </ValueIcon>
              <ValueTitle>Empatia</ValueTitle>
              <ValueText>
                Entendemos os desafios emocionais de se mudar para outro país e estamos aqui para 
                oferecer suporte em cada etapa do processo.
              </ValueText>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <FiBarChart2 size={24} />
              </ValueIcon>
              <ValueTitle>Inovação</ValueTitle>
              <ValueText>
                Utilizamos tecnologia de ponta para simplificar processos complexos e oferecer 
                soluções criativas para desafios de realocação.
              </ValueText>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <FiGlobe size={24} />
              </ValueIcon>
              <ValueTitle>Diversidade</ValueTitle>
              <ValueText>
                Celebramos as diferenças culturais e nos esforçamos para criar uma comunidade 
                inclusiva para todos os expatriados.
              </ValueText>
            </ValueCard>
          </ValuesGrid>
        </Container>
      </ValuesSection>

      {/* <TeamSection>
        <Container>
          <SectionTitle>Conheça Nosso Time</SectionTitle>
          <SectionSubtitle>
            Nossa equipe é composta por especialistas apaixonados por ajudar pessoas a realizarem 
            o sonho de viver em Portugal.
          </SectionSubtitle>
          
          <TeamGrid>
            {teamMembers.map(member => (
              <TeamMemberCard 
                key={member.id}
                name={member.name}
                role={member.role}
                bio={member.bio}
                photo={member.photo}
              />
            ))}
          </TeamGrid>
        </Container>
      </TeamSection> */}

      {/* <CtaSection>
        <Container>
          <CtaTitle>Pronto para começar sua jornada para Portugal?</CtaTitle>
          <CtaText>
            Seja você um indivíduo buscando realocação ou uma empresa especializada, temos a solução 
            perfeita para suas necessidades.
          </CtaText>
          
          <div style={{ display: 'flex', gap: spacing.lg, justifyContent: 'center' }}>
            <Button primary large>
              Quero me realocar
            </Button>
            <Button secondary large>
              Sou empresa/consultor
            </Button>
          </div>
          
          <SocialLinks>
            <SocialLink href="#" aria-label="LinkedIn">
              <FaLinkedin />
            </SocialLink>
            <SocialLink href="#" aria-label="Twitter">
              <FaTwitter />
            </SocialLink>
            <SocialLink href="#" aria-label="Instagram">
              <FaInstagram />
            </SocialLink>
          </SocialLinks>
        </Container>
      </CtaSection> */}
    </AboutUsContainer>
  );
};

export default AboutUsPage;